import { useMemo } from 'react';
import { SEARCH_TYPE } from '@/constants/searchType';
import {
  BasicSearchAnimalFields,
  BasicSearchDetailAnimalFields,
  BasicSearchMemberFields,
  getAllFieldOptionsForSearchType,
  parseAnimalSearchFieldsExclude,
  parseAnimalSearchFieldsSetting,
  parseMemberSearchFieldsSetting,
} from '../constants';
import { EBV_TAB_VALUE } from './basicSearchDialogUtils';
import type { EbvConfigItem } from '@/hooks/publicSearch/ebv/ebvColumns';
import type { Society } from '@/publicSearch/store/usePublicSearchContext';

interface UseBasicSearchFieldsParams {
  searchType: string;
  isDetailAnimalsSearch: boolean;
  tabValue: string;
  societySettings: Society['settings'];
  effectiveEbvConfig: EbvConfigItem[];
  ebvLabel: string;
  showPercentileSearch: boolean;
}

export function useBasicSearchFields({
  searchType,
  isDetailAnimalsSearch,
  tabValue,
  societySettings,
  effectiveEbvConfig,
  ebvLabel,
  showPercentileSearch,
}: UseBasicSearchFieldsParams) {
  const excludeFields = useMemo(
    () => parseAnimalSearchFieldsExclude(societySettings),
    [societySettings],
  );

  const fields = useMemo(() => {
    if (searchType === SEARCH_TYPE.ANIMAL || isDetailAnimalsSearch) {
      const base =
        searchType === SEARCH_TYPE.ANIMAL
          ? BasicSearchAnimalFields
          : BasicSearchDetailAnimalFields;
      const extraFields = parseAnimalSearchFieldsSetting(societySettings);
      const serverFieldByValue = new Map(extraFields.map((f) => [f.value, f]));
      const serverFieldByLabel = new Map(
        extraFields.map((f) => [f.label.toLowerCase().trim(), f]),
      );
      const usedServerValues = new Set<string>();
      const merged = base.map((f) => {
        const byValue = serverFieldByValue.get(f.value);
        if (byValue) {
          usedServerValues.add(byValue.value);
          return byValue;
        }
        const byLabel = serverFieldByLabel.get(f.label.toLowerCase().trim());
        if (byLabel) {
          usedServerValues.add(byLabel.value);
          return byLabel;
        }
        return f;
      });
      const extra = extraFields.filter((f) => !usedServerValues.has(f.value));
      return [...merged, ...extra].filter((f) => !excludeFields.has(f.value));
    }
    if (searchType === SEARCH_TYPE.MEMBERS) {
      const extraFields = parseMemberSearchFieldsSetting(societySettings);
      const serverFieldByValue = new Map(extraFields.map((f) => [f.value, f]));
      const serverFieldByLabel = new Map(
        extraFields.map((f) => [f.label.toLowerCase().trim(), f]),
      );
      const usedServerValues = new Set<string>();
      const merged = BasicSearchMemberFields.map((f) => {
        const byValue = serverFieldByValue.get(f.value);
        if (byValue) {
          usedServerValues.add(byValue.value);
          return byValue;
        }
        const byLabel = serverFieldByLabel.get(f.label.toLowerCase().trim());
        if (byLabel) {
          usedServerValues.add(byLabel.value);
          return byLabel;
        }
        return f;
      });
      const extra = extraFields.filter((f) => !usedServerValues.has(f.value));
      return [...merged, ...extra];
    }
    return [];
  }, [isDetailAnimalsSearch, searchType, societySettings, excludeFields]);

  const fieldValues = useMemo(() => {
    if (searchType === SEARCH_TYPE.ANIMAL || isDetailAnimalsSearch) {
      // Include EBV fields if on EBV tab
      if (tabValue === EBV_TAB_VALUE) {
        const ebvFieldValues: string[] = [];
        effectiveEbvConfig
          .filter((item) => item && item.display > 0 && item.field)
          .forEach((item) => {
            const fieldNumber = item.field.replace('ebv', '');
            // Use lowercase to match GraphQL schema field names
            ebvFieldValues.push(`ebv${fieldNumber}`); // EBV value field
            ebvFieldValues.push(`ebv${fieldNumber}Acc`); // EBV accuracy field
            ebvFieldValues.push(`ebv${fieldNumber}Percentile`); // EBV percentile field
          });
        return ebvFieldValues;
      }
      return fields.map(({ value }) => value);
    }
    if (searchType === SEARCH_TYPE.MEMBERS)
      return BasicSearchMemberFields.map(({ value }) => value);
    return [];
  }, [searchType, tabValue, effectiveEbvConfig, fields, isDetailAnimalsSearch]);

  const allFixedFieldValues = useMemo(() => {
    const values = new Set<string>();
    fields.forEach((f) => values.add(f.value));
    if (searchType === SEARCH_TYPE.ANIMAL || isDetailAnimalsSearch) {
      effectiveEbvConfig
        .filter((item) => item && item.display > 0 && item.field)
        .forEach((item) => {
          const n = item.field.replace('ebv', '');
          values.add(`ebv${n}`);
          values.add(`ebv${n}Acc`);
          values.add(`ebv${n}Percentile`);
        });
    }
    return values;
  }, [fields, effectiveEbvConfig, searchType, isDetailAnimalsSearch]);

  const dynamicFieldOptions = useMemo(
    () =>
      // For members, MEMBER_SEARCH_FIELDS (SocietySettingType=19) drives
      // both which fields appear and their labels — same source feeding
      // the advanced filter pill dropdown.
      getAllFieldOptionsForSearchType(
        searchType,
        fields,
        effectiveEbvConfig,
        allFixedFieldValues,
        societySettings,
        ebvLabel,
        showPercentileSearch,
      ),
    [
      searchType,
      fields,
      effectiveEbvConfig,
      allFixedFieldValues,
      societySettings,
      ebvLabel,
      showPercentileSearch,
    ],
  );

  return { fields, fieldValues, allFixedFieldValues, dynamicFieldOptions };
}
